<template>
  <v-container v-if="presentation"
               fluid>
    <div class="d-flex"
         style="height: 100vh;">
      <div ref="pageColumn"
           style="flex: 0 0 160px; height: 100%; overflow-y: auto;"
           class="mr-2 d-flex flex-column align-center">
        <div v-for="(p, index) in pages"
             :key="p.id"
             :id="`page_${index+1}`"
             class="mb-4"
             :class="{'font-weight-bold': (index+1) === page}"
             style="cursor: pointer; width: 130px;"
             @click.prevent="gotoPage(index + 1)">
          <div>
            <v-img :src="p.url"
                   width="130px"
                   contain
                   class="border-gray-light"
                   style="margin: 0 auto;">
            </v-img>
          </div>
          <div class="text-uppercase text-caption d-flex justify-space-between"
               :class="{'font-weight-bold': (index+1) === page}">
            <div>
              Slide {{ index + 1 }}
            </div>
            <div v-if="pageHasNote(index+1)">
              <v-icon x-small>
                fas fa-fw fa-sticky-note
              </v-icon>
            </div>
          </div>
        </div>
      </div>
      <div style="flex: 1;">
        <v-card>
          <v-card-title>
            {{ presentation.name }}
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text>
            <v-row>
              <v-col cols="12"
                     sm="8"
                     md="6">
                <div class="text-h5">
                  Current Slide
                </div>
                <div class="d-flex justify-center">
                  <div class="border-gray-light">
                    <v-img v-if="currentPage"
                           :src="currentPage.url"
                           max-width="400px"
                           contain>
                    </v-img>
                  </div>
                </div>
                <div v-if="currentSlide"
                     class="d-flex justify-center text-caption">
                  {{ currentSlide.name }}
                </div>
                <div class="d-flex justify-center mt-2">
                  <v-btn icon
                         class="me-2"
                         @click.prevent="gotoPreviousPage"
                         :disabled="page <= 1">
                    <v-icon>
                      fas fa-fw fa-chevron-left
                    </v-icon>
                  </v-btn>

                  <div>
                    <div>
                      Slide {{ page }} of {{ presentation.num_pages }}
                    </div>
                    <v-progress-linear :value="progress"
                                       height="10">
                    </v-progress-linear>
                  </div>

                  <v-btn icon
                         class="me-2"
                         @click.prevent="gotoNextPage"
                         :disabled="page >= presentation.num_pages">
                    <v-icon>
                      fas fa-fw fa-chevron-right
                    </v-icon>
                  </v-btn>
                </div>

                <div>
                  <v-textarea v-model="form.content"
                              label="Click to add notes"
                              rows="5"
                              no-resize
                              filled
                              outlined
                              @blur="saveNote">
                  </v-textarea>
                </div>
              </v-col>
              <v-divider vertical>
              </v-divider>

              <v-col cols="12"
                     sm="4"
                     md="6">
                <div v-if="nextPage">
                  <div class="text-h5">
                    Next Slide
                  </div>
                  <div class="d-flex justify-center">
                    <div class="border-gray-light">
                      <v-img v-if="nextPage"
                             :src="nextPage.url"
                             width="130px"
                             contain>
                      </v-img>
                    </div>
                  </div>
                  <div v-if="nextSlide"
                       class="d-flex justify-center text-caption">
                    {{ nextSlide.name }}
                  </div>
                  <div class="mt-2">
                    <v-textarea v-model="nextForm.content"
                                label="Click to add notes"
                                rows="5"
                                no-resize
                                filled
                                outlined
                                @blur="saveNextNote">
                    </v-textarea>
                  </div>
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </div>
    </div>
  </v-container>
</template>
<script>
import {Form} from 'vform';
import presentationService from '../../services/presentationService';
import presentationNoteService from '../../services/presentationNoteService';
import {find, isInteger, isObject, isString} from 'lodash';
import slideDeckService from '../../services/slideDeckService';

export default {
  data() {
    return {
      presentation: null,
      note: null,
      notes: [],
      pages: [],
      pageNumber: 1,
      form: new Form({
        presentation_id: null,
        content: null,
        page: 1,
      }),
      nextForm: new Form({
        presentation_id: null,
        content: null,
        page: 2,
      }),
      presentationSlides: [],
      currentSlide: null,
      nextSlide: null,
    }
  },
  computed: {
    presentationID() {
      return parseInt(this.$route.params.presentationID);
    },
    page() {
      if (this.$route.query.page) {
        try {
          return parseInt(this.$route.query.page);
        } catch (e) {
        }
      }
      return null;
    },
    progress() {
      if (this.presentation.num_pages > 0) {
        return Math.floor(this.page / this.presentation.num_pages * 100);
      }
      return null;
    },
    currentPage() {
      if (this.pages.length >= this.page) {
        return this.pages[this.page - 1];
      }
      return null;
    },
    nextPage() {
      if (this.pages.length >= this.page + 1) {
        return this.pages[this.page];
      }
    },
    slides() {
      return this.presentationSlides.map(ps => ps.slide);
    }
  },
  created() {
    this.fetchData();
  },
  mounted() {
    window.addEventListener('message', this.handleMessage);
  },
  beforeDestroy() {
    // Clean up the event listener when the component is destroyed
    window.removeEventListener('message', this.handleMessage);
  },
  methods: {
    async fetchData() {
      this.presentation = await presentationService.getPresentation(this.presentationID);
      this.form.presentation_id = this.presentation.id;
      this.nextForm.presentation_id = this.presentation.id;
      this.pages = await presentationService.getPageFilesFor(this.presentationID);
      this.presentationSlides = await slideDeckService.getPresentationSlidesFor(this.presentationID);
      await this.fetchNotes();
      if (this.page) {
        this.pageNumber = this.page;
        this.findCurrentNote();
      } else {
        this.setNote(null);
      }
    },
    async fetchNotes() {
      this.notes = await presentationNoteService.getMyNotesForPresentation(this.presentationID);
    },
    async handleMessage(event) {
      if (event.origin !== window.location.origin) {
        return; // Ignore messages from different origins
      }
      if (isObject(event.data) && event.data.type === 'changePage' && isInteger(event.data.pageNumber)) {
        //console.log('Handling incoming message', event);
        this.pageNumber = event.data.pageNumber;
        if (this.pageNumber !== this.page) {
          const route = {
            name: this.$route.name,
            params: {
              presentationID: this.$route.params.presentationID,
            },
            query: {
              page: this.pageNumber,
            }
          }
          await this.$router.push(route);
        }
        this.findCurrentNote();
      }
    },
    findCurrentNote() {
      const note = this.findNoteForPage(this.pageNumber);
      this.setNote(note);
      const nextNote = this.findNoteForPage(this.pageNumber + 1);
      this.setNextNote(nextNote);

      this.currentSlide = this.findSlideForPage(this.pageNumber);
      this.nextSlide = this.findSlideForPage(this.pageNumber + 1);
    },
    findNoteForPage(page) {
      return find(this.notes, n => n.page === page);
    },
    findSlideForPage(page) {
      if (this.slides.length >= page) {
        return this.slides[page - 1];
      }
      return null;
    },
    setNote(note) {
      if (isObject(note) && isInteger(note.id) && isString(note.content) && isInteger(note.page)) {
        this.note = note;
        this.form.page = note.page;
        this.form.content = note.content;
      } else {
        this.note = null;
        this.form.content = null;
        this.form.page = this.pageNumber;
      }
      const pageColumn = this.$refs.pageColumn;
      const targetElement = document.getElementById(`page_${this.page}`);
      if (pageColumn && targetElement) {
        //targetElement.scrollIntoView({behavior: 'smooth', block: 'start', inline: 'nearest'});
        pageColumn.scrollTop = targetElement.offsetTop;
      }
    },
    setNextNote(note) {
      if (isObject(note) && isInteger(note.id) && isString(note.content) && isInteger(note.page)) {
        this.nextForm.page = note.page;
        this.nextForm.content = note.content;
      } else {
        this.nextForm.content = null;
        this.nextForm.page = this.pageNumber;
      }
    },
    saveNote() {
      if (this.form.content) {
        this.form.post(`/api/presentation-notes`)
          .then(response => {
            this.fetchNotes();
            this.setNote(response.data);
            this.$toast.success('Notes saved successfully.');
          })
          .catch(err => {

          })
      } else {
        this.form.delete(`/api/presentations/${this.presentation.id}/notes/${this.page}`)
          .then(response => {
            this.fetchNotes();
            this.$toast.success('Notes deleted.');
          })
          .catch(err => {

          })
      }
    },
    saveNextNote() {
      if (this.nextForm.content) {
        this.nextForm.post(`/api/presentation-notes`)
          .then(response => {
            this.fetchNotes();
            this.setNextNote(response.data);
            this.$toast.success('Notes saved successfully.');
          })
          .catch(err => {

          })
      } else {
        this.nextForm.delete(`/api/presentations/${this.presentation.id}/notes/${this.nextPage}`)
          .then(response => {
            this.fetchNotes();
            this.$toast.success('Notes deleted.');
          })
          .catch(err => {

          })
      }
    },
    gotoPreviousPage() {
      this.gotoPage(this.page - 1);
    },
    gotoNextPage() {
      this.gotoPage(this.page + 1);
    },
    gotoPage(page) {
      if (page > 0 && page <= this.presentation.num_pages) {
        if (window.opener) {
          const data = {
            type: 'gotoPage',
            page: page,
          }
          window.opener.postMessage(data, window.location.origin);
        }
      }
    },
    pageHasNote(page) {
      const note = this.findNoteForPage(page);
      return !!note;
    }
  }
}
</script>
